import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import type { DirectedScript } from 'fym-shared/src/music24/directedVideoApi';
import type { DirectedActionError } from '../../directedErrors';
import {
  directedVideoApi,
  useLazyGetDirectedVideoPriceQuery,
  useRewriteDirectedShotMutation,
  useSaveDirectedScriptMutation,
  useWriteDirectedScriptMutation,
  type DirectedVideoArgs,
} from '../../directedVideoApi';
import { useDirectedAction } from '../director/useDirectedAction';

/**
 * The writes of the script step (design e1, e2): save the script (`PUT …/script` with the revision
 * on screen), rewrite one shot, rewrite the whole script, and "See the price" (the price read moves
 * the video to `priced`, then the video is read again).
 *
 * A save with an old revision answers 409 `M24_DIRECTED_STALE`: the hook reads the video again at
 * once and answers `stale`, so the step can keep the artist's text in a draft.
 */
export function useScriptActions(args: DirectedVideoArgs, scriptRevision: number) {
  const dispatch = useDispatch();
  const [saveScript, saving] = useSaveDirectedScriptMutation();
  const [rewriteShot] = useRewriteDirectedShotMutation();
  const [writeScript] = useWriteDirectedScriptMutation();
  const [fetchPrice, priceRead] = useLazyGetDirectedVideoPriceQuery();
  const action = useDirectedAction();
  const { attempt } = action;

  const reload = useCallback(() => {
    dispatch(directedVideoApi.util.invalidateTags([{ type: 'DirectedVideo', id: args.uuid }]));
  }, [args.uuid, dispatch]);

  const save = useCallback(
    async (script: DirectedScript): Promise<DirectedActionError | null> => {
      const refusal = await attempt(() => saveScript({ ...args, scriptRevision, script }).unwrap());
      if (refusal === 'stale') {
        reload();
      }
      return refusal;
    },
    [args, attempt, reload, saveScript, scriptRevision]
  );

  const rewrite = useCallback(
    (shotId: string, note?: string) => attempt(() => rewriteShot({ ...args, shotId, note }).unwrap()),
    [args, attempt, rewriteShot]
  );

  const rewriteAll = useCallback(
    (note?: string) => attempt(() => writeScript({ ...args, note }).unwrap()),
    [args, attempt, writeScript]
  );

  const seePrice = useCallback(async (): Promise<boolean> => {
    const refusal = await attempt(() => fetchPrice(args).unwrap());
    reload();
    return refusal === null;
  }, [args, attempt, fetchPrice, reload]);

  return useMemo(
    () => ({
      save,
      rewrite,
      rewriteAll,
      seePrice,
      isSaving: saving.isLoading,
      isPricing: priceRead.isFetching,
      isRunning: action.isRunning,
      error: action.error,
      clearError: action.clearError,
    }),
    [
      action.clearError,
      action.error,
      action.isRunning,
      priceRead.isFetching,
      rewrite,
      rewriteAll,
      save,
      saving.isLoading,
      seePrice,
    ]
  );
}
